import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Store } from '@ngxs/store';
import { UserFiltersDTO } from '@wilson/api/gateway';
import { AuthState } from '@wilson/auth/core';
import { FilterKey, FilterService } from '@wilson/filter';
import {
  OperationStatus,
  PublicationStatus,
  ShiftConfirmationStatus,
  TimelineActiveFilterRegionEnum,
  UserTimelines,
} from '@wilson/interfaces';
import { LocationsStoreService } from '@wilson/locations';
import {
  resetAllWilsonState,
  WilsonState,
} from '@wilson/non-domain-specific/decorators/wilson-state';
import { LocationNamePipe } from '@wilson/pipes';
import {
  FilterSettingsState,
  SaveFilterParamQueriesInCache,
  ShiftTimelinePreferredSettingsState,
} from '@wilson/preferred-settings/state';
import { endOfDay, startOfDay } from 'date-fns';
import { isEqual } from 'lodash';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  Observable,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { TimelineFetchUsersWithFilters } from '../shift-timeline-data/shift-timeline-data.actions';
import { ShiftTimelineDataState } from '../shift-timeline-data/shift-timeline-data.state';

interface Filters {
  userIds: string[];
  roleIds: string[];
  professionIds: string[];
  userLabels: string[];
  orgUnitIds: string[];
  homeLocationIds: string[];
  absencesCategoryIds: string[];
  shiftCategoryIds: string[];
  shiftOperationalStatus: OperationStatus[];
  releaseStatus: PublicationStatus | null;
  shiftLabels: string[];
  projects: string[];
  confirmedDeclined: ShiftConfirmationStatus[];
  shiftOrgUnitIds: string[];
  shiftProfessionIds: string[];
  agreementIds: string[];
  timelineActiveFilterRegion: TimelineActiveFilterRegionEnum[];
  shiftSearchString: string;
}

export type ShiftTimelineFilterKeys = Extract<
  FilterKey,
  | 'confirmedDeclined'
  | 'agreements'
  | 'absences'
  | 'users'
  | 'role'
  | 'orgUnit'
  | 'labels'
  | 'profession'
  | 'homeLocation'
  | 'releaseStatus'
  | 'acceptanceStatus'
  | 'shiftOperationalStatus'
  | 'projects'
  | 'shiftCategory'
  | 'shiftOrgUnit'
  | 'shiftLabels'
  | 'shiftProfession'
  | 'renderUnmatchedElements'
  | 'timelineActiveFilterRegion'
  | 'timelineAssignedRegionShiftSearch'
>;
@Injectable({
  providedIn: 'root',
})
export class ShiftTimelineFilterStateService {
  private store = inject(Store);
  private route = inject(ActivatedRoute);
  private locationNamePipe = inject(LocationNamePipe);
  private locationsStoreService = inject(LocationsStoreService);
  private filterService = inject(FilterService);

  public static readonly DEFAULT_TIMELINE_ACTIVE_FILTER_REGION = [
    TimelineActiveFilterRegionEnum.AssignedRegion,
  ];
  private readonly shiftTimelineFilterKeys: ShiftTimelineFilterKeys[] = [
    'agreements',
    'confirmedDeclined',
    'absences',
    'users',
    'role',
    'orgUnit',
    'labels',
    'profession',
    'homeLocation',
    'releaseStatus',
    'acceptanceStatus',
    'shiftOperationalStatus',
    'projects',
    'shiftCategory',
    'shiftOrgUnit',
    'shiftLabels',
    'shiftProfession',
    'renderUnmatchedElements',
    'timelineActiveFilterRegion',
    'timelineAssignedRegionShiftSearch',
  ] as const;

  @WilsonState<string[]>([])
  userIds!: string[];
  userIds$!: Observable<string[]>;

  @WilsonState<string[]>([])
  orgUnitIds!: string[];
  orgUnitIds$!: Observable<string[]>;

  @WilsonState<string[]>([])
  homeLocationIds!: string[];
  homeLocationIds$!: Observable<string[]>;

  @WilsonState<string[]>([])
  agreementIds!: string[];
  agreementIds$!: Observable<string[]>;

  @WilsonState<string[]>([])
  shiftOrgUnitIds!: string[];
  shiftOrgUnitIds$!: Observable<string[]>;

  @WilsonState<string[]>([])
  roleIds!: string[];
  roleIds$!: Observable<string[]>;

  @WilsonState<string[]>([])
  absencesCategoryIds!: string[];
  absencesCategoryIds$!: Observable<string[]>;

  @WilsonState<string[]>([])
  shiftCategoryIds!: string[];
  shiftCategoryIds$!: Observable<string[]>;

  @WilsonState<OperationStatus[]>([])
  shiftOperationalStatus!: OperationStatus[];
  shiftOperationalStatus$!: Observable<OperationStatus[]>;

  @WilsonState<PublicationStatus | null>(null)
  releaseStatus!: PublicationStatus | null;
  releaseStatus$!: Observable<PublicationStatus | null>;

  @WilsonState<string | null>(null)
  searchTerm!: string | null;
  searchTerm$!: Observable<string | null>;

  @WilsonState<string[]>([])
  professionIds!: string[];
  professionIds$!: Observable<string[]>;

  @WilsonState<string[]>([])
  shiftProfessionIds!: string[];
  shiftProfessionIds$!: Observable<string[]>;

  @WilsonState<string[]>([])
  labels!: string[];
  labels$!: Observable<string[]>;

  @WilsonState<string[]>([])
  shiftLabels!: string[];
  shiftLabels$!: Observable<string[]>;

  @WilsonState<string[]>([])
  projectIds!: string[];
  projectIds$!: Observable<string[]>;

  @WilsonState<ShiftConfirmationStatus[]>([])
  confirmedDeclined!: ShiftConfirmationStatus[];
  confirmedDeclined$!: Observable<ShiftConfirmationStatus[]>;

  @WilsonState(false)
  showFilterDrawer!: boolean;
  showFilterDrawer$!: Observable<boolean>;

  @WilsonState(true)
  renderUnmatchedElements!: boolean;
  renderUnmatchedElements$!: Observable<boolean>;

  @WilsonState<string>('')
  shiftSearchString!: string;
  shiftSearchString$!: Observable<string>;

  @WilsonState<TimelineActiveFilterRegionEnum[]>([
    TimelineActiveFilterRegionEnum.AssignedRegion,
  ])
  timelineActiveFilterRegion!: TimelineActiveFilterRegionEnum[];
  timelineActiveFilterRegion$!: Observable<TimelineActiveFilterRegionEnum[]>;

  readonly filters$: Observable<Filters> = combineLatest([
    this.orgUnitIds$,
    this.homeLocationIds$,
    this.agreementIds$,
    this.userIds$,
    this.roleIds$,
    this.professionIds$,
    this.labels$,
    this.absencesCategoryIds$,
    this.shiftCategoryIds$,
    this.shiftOperationalStatus$,
    this.releaseStatus$,
    this.shiftLabels$,
    this.projectIds$,
    this.confirmedDeclined$,
    this.shiftOrgUnitIds$,
    this.shiftProfessionIds$,
    this.renderUnmatchedElements$,
    this.timelineActiveFilterRegion$,
    this.shiftSearchString$,
  ]).pipe(
    map(
      ([
        orgUnitIds,
        homeLocationIds,
        agreementIds,
        userIds,
        roleIds,
        professionIds,
        userLabels,
        absencesCategoryIds,
        shiftCategoryIds,
        shiftOperationalStatus,
        releaseStatus,
        shiftLabels,
        projects,
        confirmedDeclined,
        shiftOrgUnitIds,
        shiftProfessionIds,
        renderUnmatchedElements,
        timelineActiveFilterRegion,
        shiftSearchString,
      ]) => ({
        userIds,
        roleIds,
        professionIds,
        userLabels,
        orgUnitIds,
        homeLocationIds,
        agreementIds,
        absencesCategoryIds,
        shiftCategoryIds,
        shiftOperationalStatus,
        releaseStatus,
        shiftLabels,
        projects,
        confirmedDeclined,
        shiftOrgUnitIds,
        shiftProfessionIds,
        renderUnmatchedElements,
        timelineActiveFilterRegion,
        shiftSearchString,
      }),
    ),
    debounceTime(10),
  );

  private usersDictionary$: Observable<Record<string, UserTimelines>> =
    this.store.select(ShiftTimelineDataState.usersDictionary).pipe(
      distinctUntilChanged(
        (
          previousValue: Record<string, UserTimelines>,
          currentValue: Record<string, UserTimelines>,
        ) => {
          return (
            Object.keys(previousValue).length ===
            Object.keys(currentValue).length
          );
        },
      ),
    );

  private filterUserIds$ = combineLatest([
    this.store
      .select(ShiftTimelineDataState.visibleDateRange)
      .pipe(filter((dateRange) => !!dateRange.start && !!dateRange.end)),
    this.filters$,
  ]).pipe(
    debounceTime(50),
    distinctUntilChanged(
      (
        [previousVisibleDateRange, previousFilters],
        [newVisibleDateRange, newFilters],
      ) => {
        const isStartDateSame =
          previousVisibleDateRange.start &&
          newVisibleDateRange.start &&
          previousVisibleDateRange.start.toString() ===
            newVisibleDateRange.start.toString();

        const isEndDateSame =
          previousVisibleDateRange.end &&
          newVisibleDateRange.end &&
          previousVisibleDateRange.end.toString() ===
            newVisibleDateRange.end.toString();

        const areFiltersSame = isEqual(previousFilters, newFilters);

        const response = isStartDateSame && isEndDateSame && areFiltersSame;

        return !!response;
      },
    ),
    switchMap(([dateRange, filters]) => {
      const filterOptions: UserFiltersDTO = {
        from: startOfDay(dateRange.start).toISOString(),
        to: endOfDay(dateRange.end).toISOString(),
      };

      const userFilters = this.createUserFilters(filters);
      const hasUserFilters = userFilters && Object.keys(userFilters).length > 0;

      if (hasUserFilters) {
        filterOptions.userFilters = userFilters;
      }

      const shiftFilters = this.createShiftFilters(
        filters,
        TimelineActiveFilterRegionEnum.AssignedRegion,
      );
      const absenceFilters = this.createAbsenceFilters(filters);

      if (shiftFilters && Object.keys(shiftFilters).length > 0) {
        filterOptions.shiftFilters = shiftFilters;
      }
      if (absenceFilters && Object.keys(absenceFilters).length > 0) {
        filterOptions.absenceFilters = absenceFilters;
      }

      this.store.dispatch(new TimelineFetchUsersWithFilters(filterOptions));

      return this.store.select(ShiftTimelineDataState.filterUserIds);
    }),
  );

  readonly filteredUsers$ = combineLatest([
    this.usersDictionary$,
    this.filterUserIds$,
    this.searchTerm$.pipe(debounceTime(300)),
  ]).pipe(
    switchMap(([usersDictionary, filterUserIds, searchTerm]) => {
      const lowerCaseSearchTerm = searchTerm?.toLowerCase();
      const users: UserTimelines[] = Object.values(usersDictionary);
      const filteredUsers: UserTimelines[] = [];

      return combineLatest(
        users.map(async (user) => {
          const isUserInFilterUserIds = filterUserIds.includes(
            user.id as string,
          );
          let isUserInSearchTerm = true;

          if (lowerCaseSearchTerm) {
            isUserInSearchTerm =
              user.firstName.toLowerCase().includes(lowerCaseSearchTerm) ||
              user.lastName.toLowerCase().includes(lowerCaseSearchTerm);

            const userId = this.store.selectSnapshot(AuthState.userId);
            const isHomeLocationKpiEnabled = userId
              ? this.store.selectSnapshot(
                  ShiftTimelinePreferredSettingsState.showHomeLocation(userId),
                )
              : false;

            if (isHomeLocationKpiEnabled) {
              let homeLocationName = '';
              if (user.homeLocation) {
                homeLocationName = this.locationNamePipe.transform(
                  user.homeLocation,
                  true,
                );
              } else {
                const organizationalUnitLocation = await firstValueFrom(
                  this.locationsStoreService.getLocationFromCache(
                    user.organizationalUnit.locationId as string | null,
                  ),
                );
                homeLocationName = this.locationNamePipe.transform(
                  organizationalUnitLocation,
                  true,
                );
              }

              isUserInSearchTerm =
                isUserInSearchTerm ||
                homeLocationName.toLowerCase().includes(lowerCaseSearchTerm);
            }
          }

          if (isUserInFilterUserIds && isUserInSearchTerm) {
            filteredUsers.push(user);
          }
          return user;
        }),
      ).pipe(map(() => filteredUsers));
    }),
    shareReplay(1),
  );

  public async generateShiftFilterOptionsForUnassignedRegion(
    startDate: Date,
    endDate: Date,
  ): Promise<UserFiltersDTO> {
    const options: UserFiltersDTO = {
      from: startOfDay(startDate).toISOString(),
      to: endOfDay(endDate).toISOString(),
    };
    const filters = await firstValueFrom(this.filters$);
    const shiftFilters = this.createShiftFilters(
      filters,
      TimelineActiveFilterRegionEnum.UnassignedRegion,
    );

    if (shiftFilters && Object.keys(shiftFilters).length > 0) {
      options.shiftFilters = shiftFilters;
    }

    return options;
  }

  public async generateFilterOptionsForAssignedRegion(
    startDate: Date,
    endDate: Date,
    userIds: string[] | null,
  ): Promise<UserFiltersDTO> {
    const options: UserFiltersDTO = {
      from: startOfDay(startDate).toISOString(),
      to: endOfDay(endDate).toISOString(),
    };
    const filters = await firstValueFrom(this.filters$);
    const userFilters = this.createUserFilters(filters);
    const shiftFilters = this.createShiftFilters(
      filters,
      TimelineActiveFilterRegionEnum.AssignedRegion,
    );
    const absenceFilters = this.createAbsenceFilters(filters);

    if (userFilters && Object.keys(userFilters).length > 0) {
      options.userFilters = userFilters;
    }
    if (shiftFilters && Object.keys(shiftFilters).length > 0) {
      options.shiftFilters = shiftFilters;
    }
    if (absenceFilters && Object.keys(absenceFilters).length > 0) {
      options.absenceFilters = absenceFilters;
    }

    if (userIds?.length) {
      options.userFilters = options.userFilters ?? {};
      options.userFilters.userIds = userIds;
    }

    return options;
  }

  private createUserFilters(filters: Filters): UserFiltersDTO['userFilters'] {
    const userFilters: UserFiltersDTO['userFilters'] = {};
    if (filters.roleIds.length > 0) {
      userFilters.roleIds = filters.roleIds;
    }
    if (filters.orgUnitIds.length > 0) {
      userFilters.organizationalUnitIds = filters.orgUnitIds;
    }
    if (filters.userLabels.length > 0) {
      userFilters.labelIds = filters.userLabels;
    }
    if (filters.professionIds.length > 0) {
      userFilters.professionIds = filters.professionIds;
    }
    if (filters.userIds.length > 0) {
      userFilters.userIds = filters.userIds;
    }
    if (filters.homeLocationIds.length > 0) {
      userFilters.homeLocationIds = filters.homeLocationIds;
    }
    return userFilters;
  }

  private createShiftFilters(
    filters: Filters,
    regionToFilter: TimelineActiveFilterRegionEnum,
  ): UserFiltersDTO['shiftFilters'] {
    const shiftFilters: UserFiltersDTO['shiftFilters'] = {};
    if (filters.timelineActiveFilterRegion.includes(regionToFilter)) {
      if (filters.shiftCategoryIds.length > 0) {
        shiftFilters.categoryIds = filters.shiftCategoryIds;
      }
      if (filters.shiftOperationalStatus.length > 0) {
        shiftFilters.operationalStatus = filters.shiftOperationalStatus;
      }
      if (filters.releaseStatus) {
        shiftFilters.publicationStatus = [filters.releaseStatus];
      }
      if (filters.confirmedDeclined.length > 0) {
        shiftFilters.confirmationStatus = filters.confirmedDeclined;
      }
      if (filters.projects.length > 0) {
        shiftFilters.projectIds = filters.projects;
      }
      if (filters.shiftOrgUnitIds.length > 0) {
        shiftFilters.organizationalUnitIds = filters.shiftOrgUnitIds;
      }
      if (filters.shiftLabels.length > 0) {
        shiftFilters.labelIds = filters.shiftLabels;
      }
      if (filters.shiftProfessionIds.length > 0) {
        shiftFilters.professionIds = filters.shiftProfessionIds;
      }
      if (filters.agreementIds.length > 0) {
        shiftFilters.agreementIds = filters.agreementIds;
      }
      if (filters.shiftSearchString.length > 0) {
        shiftFilters.shiftSearchString = filters.shiftSearchString;
      }
    }
    return shiftFilters;
  }

  private createAbsenceFilters(
    filters: Filters,
  ): UserFiltersDTO['absenceFilters'] {
    const absenceFilters: UserFiltersDTO['absenceFilters'] = {};
    if (filters.absencesCategoryIds.length > 0) {
      absenceFilters.categoryIds = filters.absencesCategoryIds;
    }
    return absenceFilters;
  }

  public setRenderUnmatchedElements(value: boolean): void {
    this.renderUnmatchedElements = value;
  }

  public setFilterRegion(value: TimelineActiveFilterRegionEnum[]): void {
    if (value.length === 0) {
      this.timelineActiveFilterRegion =
        ShiftTimelineFilterStateService.DEFAULT_TIMELINE_ACTIVE_FILTER_REGION;
    } else {
      this.timelineActiveFilterRegion = value;
    }
  }

  public filterCachingEventListener(): Observable<Filters> {
    return this.filters$.pipe(
      debounceTime(100),
      distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
      tap(() => {
        //setTimeout is necessary because the queryParams do not need to be updated immediately
        setTimeout(() => {
          const currentUserId = this.store.selectSnapshot(AuthState.userId);
          if (currentUserId) {
            const shiftTimelineFilters = this.pickOnlyShiftTimelineFilters(
              this.route.snapshot.queryParams,
            );
            this.store.dispatch(
              new SaveFilterParamQueriesInCache(
                currentUserId,
                shiftTimelineFilters,
              ),
            );
          }
        }, 0);
      }),
    );
  }

  public async setFilterSettings(userId: string): Promise<void> {
    const params = this.route.snapshot.queryParams;
    const isFilterSet = this.shiftTimelineFilterKeys.some((key) =>
      Object.prototype.hasOwnProperty.call(params, key),
    );

    if (!isFilterSet) {
      await this.loadFiltersFromCache(userId);
    }
  }

  protected async loadFiltersFromCache(userId: string): Promise<void> {
    const filtersInCache =
      this.store.selectSnapshot(
        FilterSettingsState.getFilterSettings(userId),
      ) || this.initializeDefaultFilters(userId);

    await this.filterService.saveValuesInQueryParam(filtersInCache);
  }

  private initializeDefaultFilters(userId: string): Partial<Filters> {
    const defaultTimelineActiveFilterRegion = {
      timelineActiveFilterRegion: `${TimelineActiveFilterRegionEnum.UnassignedRegion},${TimelineActiveFilterRegionEnum.AssignedRegion}`,
      renderUnmatchedElements: 'true',
    };

    this.store.dispatch(
      new SaveFilterParamQueriesInCache(
        userId,
        defaultTimelineActiveFilterRegion,
      ),
    );

    return this.store.selectSnapshot(
      FilterSettingsState.getFilterSettings(userId),
    );
  }

  public areTimelineFiltersSet(): boolean {
    const params = this.route.snapshot.queryParams;

    const excludedKeys: ShiftTimelineFilterKeys[] = [
      'renderUnmatchedElements',
      'timelineActiveFilterRegion',
    ];
    const containsTimelineFilters = this.shiftTimelineFilterKeys.some(
      (key) =>
        excludedKeys.indexOf(key) === -1 &&
        Object.prototype.hasOwnProperty.call(params, key),
    );

    const hasSearchTerm = !!this.searchTerm?.trim();
    return containsTimelineFilters || hasSearchTerm;
  }

  public pickOnlyShiftTimelineFilters(
    params: Params,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Record<ShiftTimelineFilterKeys, any> {
    return Object.entries(params).reduce(
      (acc, [key, value]) => {
        if (
          this.shiftTimelineFilterKeys.includes(key as ShiftTimelineFilterKeys)
        ) {
          acc[key as ShiftTimelineFilterKeys] = value;
        }
        return acc;
      },
      {} as Record<ShiftTimelineFilterKeys, any>,
    );
  }

  public resetAllWilsonState(): void {
    resetAllWilsonState(this);
  }
}
